import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { toast } from "sonner";
import { AuthLayout } from "@/components/AuthLayout";
import { GoogleButton, AuthDivider } from "@/components/GoogleButton";
import { GOOGLE_ERROR_MESSAGES } from "@/lib/authErrors";
import { SEO } from "@/components/SEO";

export default function Register() {
  const { register, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const redirect = params.get("redirect") || params.get("next") || "/dashboard";
  const oauthError = params.get("error");

  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fieldError, setFieldError] = useState({ name: "", email: "", password: "", form: "" });

  useEffect(() => {
    if (!oauthError) return;
    const msg = GOOGLE_ERROR_MESSAGES[oauthError] || "Google sign-in failed. Please try again.";
    toast.error(msg);
    params.delete("error");
    const clean = params.toString();
    navigate(clean ? `/register?${clean}` : "/register", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oauthError]);

  useEffect(() => {
    if (user && user.email) {
      navigate(redirect || "/dashboard", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFieldError({ name: "", email: "", password: "", form: "" });
    setSubmitting(true);
    try {
      await register(form.name, form.email, form.password);
      toast.success("Account created!");
      navigate(redirect);
    } catch (err) {
      const status = err.response?.status;
      const detail = formatApiErrorDetail(err.response?.data?.detail) || "Registration failed";
      // 400 → duplicate email; 422 → validation (password too short, invalid email)
      if (status === 400) setFieldError({ name: "", email: detail, password: "", form: "" });
      else if (status === 422) setFieldError({ name: "", email: "", password: detail, form: "" });
      else setFieldError({ name: "", email: "", password: "", form: detail });
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout testId="register-page">
      <SEO title="Create Account | SchengenFile" description="Create a SchengenFile account to order your Schengen visa document package." noindex />
      <h1 className="font-display font-extrabold text-3xl text-[#0f1b2d]">Create your account</h1>
      <p className="mt-2 text-sm text-[#475569]">Track orders and download your visa documents.</p>

      <div className="mt-8">
        <GoogleButton nextPath={redirect} testId="register-google-btn" />
        <AuthDivider>or continue with email</AuthDivider>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form" noValidate>
          <Field
            label="Full name"
            testid="register-name"
            value={form.name}
            onChange={(v) => setForm({ ...form, name: v })}
            error={fieldError.name}
            autoComplete="name"
          />
          <Field
            label="Email"
            type="email"
            testid="register-email"
            value={form.email}
            onChange={(v) => setForm({ ...form, email: v })}
            error={fieldError.email}
            autoComplete="email"
          />

          <div>
            <label className="text-sm font-semibold text-[#0f1b2d]">Password</label>
            <div className="relative mt-1.5">
              <input
                type={showPw ? "text" : "password"}
                required
                data-testid="register-password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                autoComplete="new-password"
                className={`w-full h-11 border rounded-md pl-3 pr-11 text-sm outline-none transition-colors focus:ring-2 focus:ring-[#2563eb]/20 ${
                  fieldError.password
                    ? "border-[#dc2626] focus:border-[#dc2626]"
                    : "border-[#e2e8f0] focus:border-[#2563eb]"
                }`}
              />
              <button
                type="button"
                onClick={() => setShowPw((s) => !s)}
                data-testid="register-password-toggle"
                aria-label={showPw ? "Hide password" : "Show password"}
                className="absolute inset-y-0 right-0 w-11 grid place-items-center text-[#64748b] hover:text-[#0f1b2d]"
              >
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="mt-1 text-xs text-[#64748b]">At least 6 characters.</p>
            {fieldError.password && (
              <p data-testid="register-password-error" className="mt-1.5 text-xs text-[#dc2626]">
                {fieldError.password}
              </p>
            )}
          </div>

          {fieldError.form && (
            <p data-testid="register-form-error" className="text-sm text-[#dc2626]">
              {fieldError.form}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid="register-submit-btn"
            className="w-full h-11 flex items-center justify-center gap-2 bg-[#2563eb] hover:bg-[#1d4ed8] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold rounded-md transition-colors"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {submitting ? "Creating…" : "Create account"}
          </button>
        </form>

        <p className="mt-4 text-xs text-[#64748b]">
          By creating an account you agree to our{" "}
          <Link to="/terms" className="text-[#2563eb] hover:underline">Terms</Link> and{" "}
          <Link to="/privacy" className="text-[#2563eb] hover:underline">Privacy Policy</Link>.
        </p>
      </div>

      <div className="mt-8 text-sm text-[#475569] text-center">
        Already have an account?{" "}
        <Link to="/login" data-testid="link-login" className="text-[#2563eb] font-semibold hover:text-[#1d4ed8]">
          Log in
        </Link>
      </div>
    </AuthLayout>
  );
}

function Field({ label, type = "text", testid, value, onChange, error, autoComplete }) {
  return (
    <div>
      <label className="text-sm font-semibold text-[#0f1b2d]">{label}</label>
      <input
        required
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        className={`mt-1.5 w-full h-11 border rounded-md px-3 text-sm outline-none transition-colors focus:ring-2 focus:ring-[#2563eb]/20 ${
          error ? "border-[#dc2626] focus:border-[#dc2626]" : "border-[#e2e8f0] focus:border-[#2563eb]"
        }`}
      />
      {error && (
        <p data-testid={`${testid}-error`} className="mt-1.5 text-xs text-[#dc2626]">
          {error}
        </p>
      )}
    </div>
  );
}
